import {
    BaseLayer,
    BaseLayerFactory_27700,
    BaseLayerFactory_3857,
    BaseLayerType
} from "@esdm/map";

import { IBaseLayer } from "@esdm/map/dist/baselayers/interfaces/IBaseLayer";
import TileLayer from "ol/layer/Tile";
import TileWMS from 'ol/source/TileWMS';

import * as olProj from 'ol/proj';
import proj4 from "proj4";
import { register } from 'ol/proj/proj4';
import Projection from "ol/proj/Projection";
import XYZ from 'ol/source/XYZ';
import OSM from 'ol/source/OSM';
import ImageLayer from "ol/layer/Image";
import ImageWMS from 'ol/source/ImageWMS';
import WMTS from 'ol/source/WMTS'; 
import WMTSTileGrid from 'ol/tilegrid/WMTS';

import { get as getProjection } from "ol/proj";
import { getWidth } from "ol/extent";
import TileGrid from "ol/tilegrid/TileGrid"

export const CheshireBaseLayerTypes = {
    ...BaseLayerType,

    CheshireTitheMaps: 992,
    CheshireOS6inchEpoch1: 993,
    CheshireOS6inchEpoch2: 994,
    CheshireAerial1945: 995,
    CheshireAerial1970: 996,
    CheshireAerial2000: 997,
    CheshireAerial: 998,
    CheshireOS: 999
};

export class CheshireBaseMapping_27700 extends BaseLayerFactory_27700 {

    public GetBaseLayerByType(baseLayerType: BaseLayerType, bingApiKey: string, esdmOsmApiKey: string): IBaseLayer {
        if (baseLayerType === CheshireBaseLayerTypes.CheshireOS) {
            return this.GetCheshireOS();
        }

        if (baseLayerType === CheshireBaseLayerTypes.CheshireAerial) {
            return this.GetCheshireAerial();
        }

        if (baseLayerType === CheshireBaseLayerTypes.CheshireAerial2000) {
            return this.GetCheshireAerial2000();
        }

        if (baseLayerType === CheshireBaseLayerTypes.CheshireAerial1970) {
            return this.GetCheshireAerial1970();
        }

        if (baseLayerType === CheshireBaseLayerTypes.CheshireAerial1945) {
            return this.GetCheshireAerial1945();
        }

        if (baseLayerType === CheshireBaseLayerTypes.CheshireOS6inchEpoch2) {
            return this.GetCheshireOS6inch_1888_1913();
        }

        if (baseLayerType === CheshireBaseLayerTypes.CheshireOS6inchEpoch1) {
            return this.GetCheshireOS6inch_1840_1880();
        }

        if (baseLayerType === CheshireBaseLayerTypes.CheshireTitheMaps) {
            return this.GetCheshireTitheMaps();
        }
        
        return super.GetBaseLayerByType(baseLayerType, bingApiKey, esdmOsmApiKey);
    }   

    private GetCheshireOS(): IBaseLayer {
        
        let lyr = new TileLayer({
            visible: true,
            source: new WMTS({
                //Configured via OGC Proxy
                url: "/api/OGCService/CheshireOS",
                layer: "Road_27700",
                matrixSet: "EPSG:27700",
                format: "image/png",
                projection: "EPSG:27700",
                tileGrid: new WMTSTileGrid({
                    origin: [-238375.0, 1376256.0],
                    resolutions: [896, 448, 224, 112, 56, 28, 14, 7, 3.5, 1.75, 0.875, 0.4375, 0.21875, 0.109375],
                    matrixIds: ["EPSG:27700:0", "EPSG:27700:1", "EPSG:27700:2", "EPSG:27700:3", "EPSG:27700:4", "EPSG:27700:5", "EPSG:27700:6", "EPSG:27700:7", "EPSG:27700:8", "EPSG:27700:9", "EPSG:27700:10", "EPSG:27700:11", "EPSG:27700:12", "EPSG:27700:13"]
                }),
                style: "default",
                crossOrigin: "Anonymous",
                attributions: [`© Crown copyright and database rights ${new Date().getFullYear()} Ordnance Survey AC0000816377.`,
                'Use of this data is subject to <a href="/map-terms-and-conditions#os">terms and conditions</a>.']
            })
        });
        return new BaseLayer("CheshireOS", "Ordnance Survey", lyr);
    } 

    private GetCheshireAerial(): IBaseLayer {
        let lyr = new TileLayer({
            visible: false,
            source: new TileWMS({
                //Configured via OGC Proxy
                url: "/api/OGCService/CheshireAerial",
                params: {
                    LAYERS: 'APGB_25CMAERIAL2019to2021',
                    FORMAT: 'image/png',
                    SRS: 'EPSG:27700'
                },
                projection: "EPSG:27700",
                crossOrigin: "Anonymous",
                attributions: ['© Bluesky International Limited and Getmapping Limited 1999 - 2020',
                    '© Bluesky International Limited 2021 and onwards.',
                    'Use of this data is subject to <a href="/map-terms-and-conditions#apgb">terms and conditions</a>']
            })
        });
        return new BaseLayer("CheshireAerial", "Aerial Photography", lyr);
    }

    private GetCheshireAerial2000(): IBaseLayer {
        let lyr = new TileLayer({
            visible: false,
            source: new TileWMS({
                //Configured via OGC Proxy
                url: "/api/OGCService/CheshireWMS",
                params: {
                    LAYERS: 'AERIAL2000',
                    FORMAT: 'image/png',
                    SRS: 'EPSG:27700'
                },
                projection: "EPSG:27700",
                crossOrigin: "Anonymous",
                attributions: ['1999-2003 © Copyright GeoPerspectives, supplied by Bluesky to Cheshire East Council and Cheshire West and Chester Council']
            })
        });
        return new BaseLayer("CheshireAerial2000", "1999 – 2003 Bluesky Air Survey", lyr);
    }

    private GetCheshireAerial1970(): IBaseLayer {
        let lyr = new TileLayer({
            visible: false,
            source: new TileWMS({
                //Configured via OGC Proxy
                url: "/api/OGCService/CheshireWMS",
                params: {
                    LAYERS: 'AERIAL1970',
                    FORMAT: 'image/png',
                    SRS: 'EPSG:27700'
                },
                projection: "EPSG:27700",
                crossOrigin: "Anonymous",
                attributions: ['1971-3 Aerial Survey of Cheshire. © Copyright 2006 Cheshire West and Chester Council & Cheshire East Council © All rights reserved']
            })
        });
        return new BaseLayer("CheshireAerial1970", "1971-3 Cheshire Air Survey", lyr);
    }

    private GetCheshireAerial1945(): IBaseLayer {
        let lyr = new TileLayer({
            visible: false,
            source: new TileWMS({
                //Configured via OGC Proxy
                url: "/api/OGCService/CheshireWMS",
                params: {
                    LAYERS: 'AERIAL1945',
                    FORMAT: 'image/png',
                    SRS: 'EPSG:27700'
                },
                projection: "EPSG:27700",
                crossOrigin: "Anonymous",
                attributions: ['Cities Revealed ® copyright by The GeoInformation ® Group, 2003 and Crown Copyright © All rights reserved ']
            })
        });
        return new BaseLayer("CheshireAerial1945", "1945-8 - RAF Air Photos", lyr);
    }

    private GetCheshireOS6inch_1840_1880(): IBaseLayer {
        let lyr = new TileLayer({
            visible: false,
            source: new TileWMS({
                //Configured via OGC Proxy
                url: "/api/OGCService/CheshireWMS",
                params: {
                    LAYERS: 'Historic_Epoch1',
                    FORMAT: 'image/png',
                    SRS: 'EPSG:27700'
                },
                projection: "EPSG:27700",
                crossOrigin: "Anonymous"
            })
        });
        return new BaseLayer("CheshireAerial1970", "1971-3 Cheshire Air Survey", lyr);
    }

    private GetCheshireOS6inch_1888_1913(): IBaseLayer {
        let lyr = new TileLayer({
            visible: false,
            source: new TileWMS({
                //Configured via OGC Proxy
                url: "/api/OGCService/CheshireWMS",
                params: {
                    LAYERS: 'Historic_Epoch2',
                    FORMAT: 'image/png',
                    SRS: 'EPSG:27700'
                },
                projection: "EPSG:27700",
                crossOrigin: "Anonymous"
            })
        });
        return new BaseLayer("CheshireAerial1970", "1971-3 Cheshire Air Survey", lyr);
    }

    private GetCheshireTitheMaps(): IBaseLayer {
        let lyr = new TileLayer({
            visible: false,
            source: new TileWMS({
                //Configured via OGC Proxy
                url: "/api/OGCService/CheshireWMS",
                params: {
                    LAYERS: 'TitheMaps',
                    FORMAT: 'image/png',
                    SRS: 'EPSG:27700'
                },
                projection: "EPSG:27700",
                crossOrigin: "Anonymous"
            })
        });
        return new BaseLayer("CheshireTitheMaps", "Tithe Maps", lyr);
    }
}